import type { FormInstance } from 'rsuite/Form'

import { useState, useRef, useCallback, useEffect, useMemo } from 'react'
import { mergeLeft } from 'merge-left-utils'

/*
  Helper hook to simplify work with rsuite forms and validation
*/

export function useValidatableForm<T extends Record<string, any>> (defaultState: T | (() => T)) {
  const [ isInitial, setIsInitial ] = useState<boolean>(false)
  const [ formState, setFormState ] = useState<T>(defaultState)
  const [ formErrors, setFormErrors ] = useState<Record<string, any>>({})
  const formRef = useRef<FormInstance | null>(null)

  const setFormRef = useCallback((ref: FormInstance) => {
    formRef.current = ref
  }, [])

  const handleFormChange = useCallback(
    (formData: Partial<T> | null | undefined) => {
      setIsInitial(false)
      if (formData && typeof formData === 'object') {
        setFormState((state) => mergeLeft(state, formData))
      }
    },
    [ setFormState, setIsInitial ]
  )

  const handleFormError = useCallback((error: any) => {
    setFormErrors(error)
  }, [])

  const resetForm = useCallback(() => {
    setFormState(defaultState)
    setIsInitial(true)
  }, [ defaultState ])

  // run form checks on state change
  useEffect(() => {
    if (
      formRef.current &&
      !isInitial &&
      formState &&
      typeof formState === 'object'
    ) {
      try {
        formRef.current.check?.()
      } 
      catch (error) {
        console.error(error)
      }
    }
  }, [ formState, isInitial ])

  return useMemo(
    () => ({
      formState,
      setFormState,
      formRef,
      setFormRef,
      formErrors,
      setFormErrors,
      handleFormChange,
      handleFormError,
      resetForm
    }),
    [
      formErrors,
      formState,
      handleFormChange,
      handleFormError,
      resetForm,
      setFormRef
    ]
  )
}
